/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useDisclosure } from "@chakra-ui/react";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { FeatureType } from "@/jenesys-types/data/enums";
import { TeamMember } from "@/jenesys-types/jenesys-entities/config";

import useActionTrigger from "@/hooks/util/useActionTrigger";
import useItemAction from "@/hooks/util/useItemAction";
import { useRoles } from "@/hooks/util/useRoles";
import {
  fetchBanks,
  loadBankConnections,
  loadChartOfAccounts,
  loadEmailStatus,
  loadIntegrationAuthorize,
  loadIntegrationDisconnect,
  loadIntegrationRefresh,
  loadMailAuthorize,
  loadPlatformStatus,
  loadWhatsappStatus,
  selectConnectionsSettings,
  selectPlatformStatusByNameData
} from "@/store/features/clientSettings/connections/connections";
import {
  loadDisconnectMember,
  loadInviteMember,
  loadTeamMembers,
  selectTeamSettings
} from "@/store/features/clientSettings/team/team";
import {
  accountingPlatforms,
  bankReconPlatforms,
  commsPlatforms,
  emailPlatforms,
  toastError,
  toastSuccess
} from "@/util/helpers";

const getPlatformDetails = (
  category: string,
  platform: string
): (typeof accountingPlatforms)[0] | undefined => {
  switch (category) {
    case "accounting":
      return accountingPlatforms[platform];

    case "comms":
      return commsPlatforms[platform];

    case "email":
      return emailPlatforms[platform];

    case "bank-reconciliation":
      return bankReconPlatforms[platform];

    default:
      return undefined;
  }
};

interface CompanyIntegrationsProps {
  companyId?: string;
  clientId?: string;
  category?: string;
  platformName?: string;
}

export const useConnectionPlatform = ({
  companyId,
  clientId,
  category,
  platformName
}: CompanyIntegrationsProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [teamPage, setTeamPage] = useState(0);
  const [coaPage, setCOAPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const whatsappDisclosure = useDisclosure();
  const disconnectMemberDisclosure = useDisclosure();
  const disconnectPendingInviteDisclosure = useDisclosure();
  const resendInviteDisclosure = useDisclosure();
  const { authorized, error } = useParams();
  const { userCan } = useRoles();

  const platform = getPlatformDetails(category!, platformName!);
  const {
    teamMembers: team,
    isTeamMembersLoading: isFetchingTeam,
    isDisconnectingMember,
    isInvitingMember
  } = useSelector(selectTeamSettings);
  const accountingStatus = useSelector(selectPlatformStatusByNameData);
  const {
    banks,
    bankConnections,
    isFetchingBankConnections,
    chartOfAccounts,
    isLoadingCOAs,
    emailStatus,
    whatsappStatus,
    isRefreshing,
    isFetchingAcctAuthURL,
    isFetchingMailAuthUrl,
    isDisconnectingIntegration
  } = useSelector(selectConnectionsSettings);

  const isAccounting = category === "accounting";
  const isBankRecon = category === "bank-reconciliation";
  const isComms = category === "comms";
  const isEmail = category === "email";
  const isWhatsapp = platformName === "whatsapp";

  useEffect(() => {
    if (authorized === "true" && platform) {
      toastSuccess(`${platform.displayName} Integration successful`);
      navigate(
        `/client/${clientId}/settings/connections/${category}/${platformName}`
      );
    } else if (authorized === "false" && error) {
      toastError(`${platform?.displayName} Integration failed`, null, error);
      navigate(
        `/client/${clientId}/settings/connections/${category}/${platformName}`
      );
    }
  }, []);

  useEffect(() => {
    if (
      (clientId || companyId) &&
      userCan("company.members.read") &&
      (isAccounting || isEmail || isComms)
    ) {
      dispatch(
        loadTeamMembers((clientId || companyId) as string, {
          searchQuery: "",
          page: teamPage
        })
      );
    }
  }, [
    dispatch,
    clientId,
    companyId,
    teamPage,
    userCan("company.members.read")
  ]);

  useEffect(() => {
    if (clientId && isAccounting) {
      dispatch(
        loadChartOfAccounts(clientId as string, {
          searchQuery,
          page: coaPage
        })
      );
    }
  }, [dispatch, clientId, searchQuery, coaPage]);

  useEffect(() => {
    if (clientId || companyId) {
      if (category === "accounting") {
        dispatch(loadPlatformStatus(clientId || companyId!));
      } else if (category === "email") {
        dispatch(loadEmailStatus(clientId || companyId!, platformName!));
      } else if (platformName === "whatsapp") {
        dispatch(loadWhatsappStatus());
      } else if (category === "bank-reconciliation") {
        if (!banks.length) dispatch(fetchBanks());
        if (!bankConnections.length)
          dispatch(loadBankConnections(clientId || companyId!));
      }
    }
  }, [dispatch, clientId, companyId]);

  const { response: companyAccountingIntegrations } = useItemAction(
    "loadCompanyAccountingIntegrations",
    { companyId: clientId },
    undefined,
    FeatureType.Integrations,
    { platform: platformName?.toUpperCase() },
    undefined,
    {
      enabled: !!clientId && isAccounting
    }
  );

  const handleDisconnectMember = () =>
    dispatch(
      loadDisconnectMember(
        clientId!!,
        selectedMember?.id!!,
        platformName!!,
        () => {
          toastSuccess("Deleted!");
          dispatch(
            loadTeamMembers(clientId as string, {
              searchQuery: "",
              page: teamPage
            })
          );
          disconnectMemberDisclosure.onClose();
          setSelectedMember(null);
        }
      )
    );

  const {
    trigger: handleDisconnectWhatsapp,
    isLoading: isDisconnectingWhatsapp
  } = useActionTrigger({
    actionName: "disconnectWhatsapp",
    pathParams: {
      connectionId:
        selectedMember?.whatsapp_connection?.id || whatsappStatus?.id
    },
    featureCluster: FeatureType.Integrations,
    onSuccess: () => {
      toastSuccess("Disconnected!");
      dispatch(loadWhatsappStatus());
      dispatch(
        loadTeamMembers((clientId || companyId) as string, {
          searchQuery: "",
          page: teamPage
        })
      );
      disconnectMemberDisclosure.onClose();
      setSelectedMember(null);
    },
    onError: (disconnectError) => {
      toastError("Unable to disconnect", disconnectError);
    }
  });

  const {
    trigger: handleDisconnectPendingWhatsapp,
    isLoading: isDisconnectingPendingWhatsapp
  } = useActionTrigger({
    actionName: "disconnectWhatsapp",
    pathParams: {
      connectionId: selectedMember?.whatsapp_connection?.id
    },
    featureCluster: FeatureType.Integrations,
    onSuccess: () => {
      toastSuccess("Invitation canceled!");
      dispatch(loadWhatsappStatus());
      dispatch(
        loadTeamMembers((clientId || companyId) as string, {
          searchQuery: "",
          page: teamPage
        })
      );
      disconnectPendingInviteDisclosure.onClose();
      setSelectedMember(null);
    },
    onError: (disconnectError) => {
      toastError("Unable to cancel invitation", disconnectError);
    }
  });

  const { trigger: handleResendInvite, isLoading: isResendingInvite } =
    useActionTrigger({
      actionName: "resendWhatsappInvite",
      pathParams: {
        connectionId: selectedMember?.whatsapp_connection?.id!!
      },
      featureCluster: FeatureType.Integrations,
      onSuccess: () => {
        toastSuccess("Invite resent!");
        resendInviteDisclosure.onClose();
        setSelectedMember(null);
      },
      onError: (resendError) => {
        toastError("Unable to resend invite", resendError);
      }
    });

  const handleSubmitAddMember = (member: TeamMember) =>
    dispatch(
      loadInviteMember(clientId!!, member.id, platformName!!, () => {
        toastSuccess("Invite Sent!");
        dispatch(
          loadTeamMembers(clientId as string, {
            searchQuery: "",
            page: teamPage
          })
        );
        setSelectedMember(null);
      })
    );

  const triggerDeleteWarning = (member: TeamMember) => {
    setSelectedMember(member);
    disconnectMemberDisclosure.onOpen();
  };

  const triggerDisconnectPendingInvite = (member: TeamMember) => {
    setSelectedMember(member);
    disconnectPendingInviteDisclosure.onOpen();
  };

  const handleTeamPageClick = (event: { selected: number }) => {
    setTeamPage(event.selected);
  };

  const handleCOAPageClick = (event: { selected: number }) => {
    setCOAPage(event.selected);
  };

  const handleInitiateConnect = () => {
    if (category === "accounting") {
      dispatch(
        loadIntegrationAuthorize(clientId!!, platformName!!, (data: any) => {
          window.location.href = data.authorization_url;
        })
      );
    } else if (platformName === "whatsapp") {
      whatsappDisclosure.onOpen();
    } else if (platformName === "gmail" || platformName === "outlook") {
      dispatch(
        loadMailAuthorize(clientId!!, platformName!!, (data: any) => {
          window.location.href = data.authorization_url;
        })
      );
    }
  };

  const afterRefresh = () => {
    dispatch(
      loadChartOfAccounts(clientId as string, {
        searchQuery,
        page: coaPage
      })
    );
    dispatch(loadPlatformStatus(clientId!));
  };

  const handleRefresh = () =>
    dispatch(
      loadIntegrationRefresh(
        clientId!!,
        platformName!!,
        () => {
          toastSuccess("Refreshed successfully!");
          afterRefresh();
        },
        () => {
          toastError("Refresh error!");
          afterRefresh();
        }
      )
    );

  const handleDisconnect = () => {
    if (platformName === "whatsapp") {
      handleDisconnectWhatsapp();
      return;
    }

    let type = "";

    if (category === "accounting") {
      type = "accounting";
    } else {
      switch (platformName) {
        case "whatsapp":
        case "gmail":
        case "outlook":
          type = platformName;
          break;
        default:
          type = "";
          break;
      }
    }
    dispatch(loadIntegrationDisconnect(clientId!, platformName!, type));
  };

  const getConnectionStatus = () => {
    if (category === "accounting") {
      return (
        accountingStatus?.user_status === "valid" &&
        accountingStatus?.company_status === "valid"
      );
    }
    if (category === "email") {
      return emailStatus?.status === "connected";
    }
    if (platformName === "whatsapp") {
      return whatsappStatus?.status === "connected";
    }
    return false;
  };

  const isConnected = getConnectionStatus();
  const isConnecting = isFetchingMailAuthUrl || isFetchingAcctAuthURL;
  const helpLink =
    platform?.helpLink ||
    "https://intercom.help/jenesys-hq-ltd/en/articles/9924986-whatsapp-connecting-jack";

  return {
    team,
    banks,
    isComms,
    isEmail,
    coaPage,
    helpLink,
    teamPage,
    platform,
    isWhatsapp,
    isBankRecon,
    isConnected,
    searchQuery,
    isConnecting,
    isAccounting,
    isRefreshing,
    isLoadingCOAs,
    whatsappStatus,
    selectedMember,
    isFetchingTeam,
    bankConnections,
    chartOfAccounts,
    accountingStatus,
    isInvitingMember,
    isResendingInvite,
    whatsappDisclosure,
    isDisconnectingMember,
    isFetchingMailAuthUrl,
    resendInviteDisclosure,
    isDisconnectingWhatsapp,
    isFetchingBankConnections,
    disconnectMemberDisclosure,
    disconnectPendingInviteDisclosure,
    isDisconnectingPendingWhatsapp,
    isDisconnectingIntegration:
      isDisconnectingIntegration ||
      isDisconnectingWhatsapp ||
      isDisconnectingPendingWhatsapp,
    companyAccountingIntegrations,
    setCOAPage,
    handleRefresh,
    setSearchQuery,
    handleDisconnect,
    setSelectedMember,
    handleResendInvite,
    handleCOAPageClick,
    handleTeamPageClick,
    triggerDeleteWarning,
    triggerDisconnectPendingInvite,
    handleInitiateConnect,
    handleSubmitAddMember,
    handleDisconnectMember,
    handleDisconnectWhatsapp,
    handleDisconnectPendingWhatsapp
  };
};
